import React, { useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { DefaultButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import { useFeatureFlags } from 'app/services/request-api';
import { isLegacyRequestHelpPreferred } from 'app/utils/requestHelpExperiencePreference';
import { setRequestHelpExperience } from 'app/utils/requestHelpExperiencePreference';
import { useGetUserPreferences } from 'app/services/user-api';
import { RequestHelpExperience } from 'generated/user-backend';

interface NewTicketFlowGateOptions {
    respectRequestHelpPreference?: boolean;
}

/**
 * Gates the new ticket-creation flows (Flow A/B) behind the EnableNewTicketFlow runtime flag.
 * While the flag is loading we render a spinner (so we don't wrongly redirect); once resolved,
 * OFF redirects to the legacy flow, while request failures remain visible and retryable.
 * Selected entry routes can also honor the user's reversible legacy-experience preference.
 */
export function withNewTicketFlowGate<P extends object>(
    Component: React.ComponentType<P>,
    options: NewTicketFlowGateOptions = {},
): React.FC<P> {
    const Gated: React.FC<P> = (props) => {
        const { data, isLoading, isError, refetch } = useFeatureFlags();
        const {
            data: userPreferences,
            isLoading: isPreferenceLoading,
            isError: isPreferenceError,
            refetch: refetchPreferences,
        } = useGetUserPreferences(Boolean(options.respectRequestHelpPreference));
        const serverPreference = userPreferences?.requestHelpExperience;

        useEffect(() => {
            if (serverPreference !== undefined) {
                setRequestHelpExperience(serverPreference);
            }
        }, [serverPreference]);

        if (isError || (options.respectRequestHelpPreference && isPreferenceError)) {
            return (
                <Stack tokens={{ childrenGap: 12 }} styles={{ root: { margin: 24 } }}>
                    <MessageBar messageBarType={MessageBarType.error}>
                        We couldn’t load your ticket-flow settings. Please try again.
                    </MessageBar>
                    <DefaultButton
                        data-testid="retry-ticket-flow-settings"
                        text="Try again"
                        onClick={() => {
                            if (isError) void refetch();
                            if (isPreferenceError) void refetchPreferences();
                        }}
                    />
                </Stack>
            );
        }
        if (isLoading || (options.respectRequestHelpPreference && isPreferenceLoading)) {
            return <Spinner size={SpinnerSize.large} styles={{ root: { height: '60vh' } }} label="Loading..." />;
        }
        const shouldUseLegacyExperience =
            options.respectRequestHelpPreference
            && (serverPreference === RequestHelpExperience.Legacy
                || (serverPreference !== RequestHelpExperience.New && isLegacyRequestHelpPreferred()));
        return data?.enableNewTicketFlow && !shouldUseLegacyExperience
            ? <Component {...props} />
            : <Redirect to="/help-request" />;
    };
    Gated.displayName = `withNewTicketFlowGate(${Component.displayName || Component.name || 'Component'})`;
    return Gated;
}
